import { AlertTriangle, Download, Plus, XCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "@/components/ui/Button";
import { CardSkeleton, SkeletonBlock } from "@/components/ui/Skeleton";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import { useRetailerSession } from "@/components/feature/retailer/RetailerSessionContext";
import { adminService, walletService } from "@/services/apiServices";
import { WALLET_RATES, setWalletRates } from "./constants";
import type { TopUpRequest, WalletBalance, WalletTxn } from "@/types/wallet";
import { DEFAULT_RANGE, formatDate, formatINR } from "./data";
import TransactionsTab from "./components/TransactionsTab";
import TopupsTab from "./components/TopupsTab";
import RequestTopupModal from "./components/RequestTopupModal";
import TopupDetailModal from "./components/TopupDetailModal";

function todayStr(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function rangeToDates(range: string): { dateFrom?: string; dateTo?: string } {
  const now = new Date();
  switch (range) {
    case "Today":
      return { dateFrom: todayStr(now), dateTo: todayStr(now) };
    case "Last 7 Days": {
      const from = new Date(now);
      from.setDate(from.getDate() - 7);
      return { dateFrom: todayStr(from), dateTo: todayStr(now) };
    }
    case "Last 30 Days": {
      const from = new Date(now);
      from.setDate(from.getDate() - 30);
      return { dateFrom: todayStr(from), dateTo: todayStr(now) };
    }
    case "This Month": {
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      return { dateFrom: todayStr(from), dateTo: todayStr(now) };
    }
    case "Last 3 Months": {
      const from = new Date(now);
      from.setMonth(from.getMonth() - 3);
      return { dateFrom: todayStr(from), dateTo: todayStr(now) };
    }
    default:
      return {};
  }
}

function escapeCsv(value: unknown): string {
  const s = String(value ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function RetailerWallet() {
  return (
    <ToastProvider>
      <WalletInner />
    </ToastProvider>
  );
}

function WalletInner() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const { retailerId, refreshWallet } = useRetailerSession();

  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState<WalletBalance | null>(null);

  const [tab, setTab] = useState<"transactions" | "topup">("transactions");

  // Transactions state
  const [txns, setTxns] = useState<WalletTxn[]>([]);
  const [txnTotal, setTxnTotal] = useState(0);
  const [txnPage, setTxnPage] = useState(1);
  const [txnPageSize, setTxnPageSize] = useState(25);
  const [txnLoading, setTxnLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(() => searchParams.get("ref") ?? "");
  const [search, setSearch] = useState(() => searchParams.get("ref") ?? "");
  const [type, setType] = useState("");
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [rangeOpen, setRangeOpen] = useState(false);

  // Top-ups state
  const [topups, setTopups] = useState<TopUpRequest[]>([]);
  const [topupTotal, setTopupTotal] = useState(0);
  const [topupPage, setTopupPage] = useState(1);
  const [topupPageSize, setTopupPageSize] = useState(10);
  const [topupLoading, setTopupLoading] = useState(true);

  const [topupOpen, setTopupOpen] = useState(() => searchParams.get("topup") === "1");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTarget, setDetailTarget] = useState<TopUpRequest | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setTxnPage(1);
  }, [search, type, range]);

  const [rates, setRates] = useState<{ creationRate: number; installmentRate: number } | null>(null);

  const loadBalance = useCallback(async () => {
    try {
      const [res, rateRes] = await Promise.allSettled([
        walletService.getBalance(retailerId),
        walletService.getRates(),
      ]);

      if (res.status === "fulfilled" && res.value?.data) {
        setBalance(res.value.data);
      }

      if (rateRes.status === "fulfilled" && rateRes.value?.data) {
        const d = rateRes.value.data;
        const cRate = Number(d.creationRate ?? d.mandateCreationFee ?? 500);
        const iRate = Number(d.installmentRate ?? d.installmentCharge ?? 40);
        const lbRate = Number(d.lowBalanceThreshold ?? 500);
        setRates({ creationRate: cRate, installmentRate: iRate });
        setWalletRates(cRate, iRate, lbRate);
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to load your wallet");
    } finally {
      setLoading(false);
    }
  }, [retailerId, toast]);

  const loadTxns = useCallback(async () => {
    setTxnLoading(true);
    try {
      const { dateFrom, dateTo } = rangeToDates(range);
      const res = await walletService.getTransactions(retailerId, {
        search,
        type,
        dateFrom,
        dateTo,
        page: txnPage,
        limit: txnPageSize,
      });
      setTxns(res?.data?.transactions ?? []);
      setTxnTotal(res?.data?.pagination?.total ?? 0);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load transactions");
      setTxns([]);
    } finally {
      setTxnLoading(false);
    }
  }, [retailerId, search, type, range, txnPage, txnPageSize, toast]);

  const loadTopups = useCallback(async () => {
    setTopupLoading(true);
    try {
      const res = await walletService.getTopups(retailerId, { page: topupPage, limit: topupPageSize });
      setTopups(res?.data?.topups ?? []);
      setTopupTotal(res?.data?.pagination?.total ?? 0);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load top-up requests");
      setTopups([]);
    } finally {
      setTopupLoading(false);
    }
  }, [retailerId, topupPage, topupPageSize, toast]);

  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  useEffect(() => {
    loadTxns();
  }, [loadTxns]);

  useEffect(() => {
    loadTopups();
  }, [loadTopups]);

  const available = balance?.availableBalance ?? 0;
  const status = balance?.status ?? "HEALTHY";
  const creationRate = rates?.creationRate ?? balance?.creationRate ?? WALLET_RATES.CREATION_CHARGE ?? 500;
  const installmentRate = rates?.installmentRate ?? balance?.installmentRate ?? WALLET_RATES.INSTALLMENT_CHARGE ?? 40;

  const remainingMandates = useMemo(
    () => (creationRate > 0 ? Math.max(0, Math.floor(available / creationRate)) : 0),
    [available, creationRate]
  );

  async function handleTopupSubmit(data: {
    amount: number;
    paymentMode: string;
    referenceNumber: string;
    receiptFileName: string;
  }) {
    const res = await walletService.requestTopup({
      retailerId,
      amount: data.amount,
      paymentMode: data.paymentMode,
      referenceNumber: data.referenceNumber,
      receiptFileName: data.receiptFileName,
    });
    toast.success(res?.message || "Top-up request submitted for approval");
    window.dispatchEvent(new CustomEvent("mandatepro_wallet_badge_updated"));
    setTab("topup");
    await Promise.all([loadTopups(), loadBalance(), refreshWallet()]);
  }

  async function handleDownloadStatement() {
    setExporting(true);
    try {
      const { dateFrom, dateTo } = rangeToDates(range);
      const res = await walletService.getTransactions(retailerId, {
        search,
        type,
        dateFrom,
        dateTo,
        page: 1,
        limit: 10000,
      });
      const rows: WalletTxn[] = res?.data?.transactions ?? [];
      if (rows.length === 0) {
        toast.error("No transactions to export");
        return;
      }
      const headers = ["Date", "Transaction ID", "Category", "Description", "Reference", "Type", "Amount", "Balance After"];
      const body = rows.map((t) => [
        formatDate(t.createdAt),
        t.txnId,
        t.category,
        t.description,
        t.refNo ?? "",
        t.type,
        t.amount,
        t.balanceAfter,
      ]);
      const csv = [headers, ...body].map((r) => r.map(escapeCsv).join(",")).join("\r\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `wallet-statement-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} transaction${rows.length === 1 ? "" : "s"}`);
    } catch (err: any) {
      toast.error(err?.message || "Could not download your statement");
    } finally {
      setExporting(false);
    }
  }

  function resetFilters() {
    setSearchInput("");
    setType("");
    setRange(DEFAULT_RANGE);
  }

  const tabBtn = "whitespace-nowrap rounded-md px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer";

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Wallet</h1>
          <p className="mt-1 text-sm text-ink-secondary">Manage your balance and view transaction history</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            onClick={handleDownloadStatement}
            loading={exporting}
            leftIcon={<Download className="h-4 w-4" />}
          >
            Download Statement
          </Button>
          <Button onClick={() => setTopupOpen(true)} leftIcon={<Plus className="h-4 w-4" />}>
            Request Top-up
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 flex flex-col gap-5">
          <SkeletonBlock className="h-40 w-full rounded-[12px]" />
          <CardSkeleton cards={4} />
          <SkeletonBlock className="h-96 w-full rounded-[10px]" />
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-6 rounded-[12px] bg-primary-500 p-6 md:grid-cols-2 md:gap-0 md:divide-x md:divide-white/20">
            <div className="md:pr-8">
              <p className="text-[13px] text-white/80">Available Balance</p>
              <p className="mt-1.5 text-[32px] font-bold leading-tight text-white">{formatINR(available)}</p>
              <p className="mt-1.5 text-xs text-white/70">
                Last recharged on {balance?.lastRechargeAt ? formatDate(balance.lastRechargeAt) : "—"}
              </p>
            </div>
            <div className="md:pl-8">
              <p className="text-[13px] text-white/80">Mandates you can create</p>
              <p className="mt-1.5 text-2xl font-semibold leading-tight text-white">{remainingMandates}</p>
              <p className="mt-1.5 text-xs text-white/70">
                At {formatINR(creationRate)} per mandate
                {(balance?.pendingTopups ?? 0) > 0 ? ` · ${balance?.pendingTopups} top-up pending approval` : ""}
              </p>
            </div>
          </div>

          {status === "BLOCKED" && (
            <div className="flex flex-col items-start justify-between gap-4 rounded-[10px] border border-danger/40 bg-danger-bg p-4 sm:flex-row sm:items-center">
              <div className="flex items-start gap-3">
                <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-danger" />
                <div>
                  <p className="text-sm font-semibold text-ink">Mandate creation is blocked</p>
                  <p className="mt-0.5 text-[13px] text-ink-secondary">
                    A minimum balance of {formatINR(creationRate)} is required to create a mandate. Please request a
                    top-up.
                  </p>
                </div>
              </div>
              <Button onClick={() => setTopupOpen(true)} leftIcon={<Plus className="h-4 w-4" />}>
                Request Top-up
              </Button>
            </div>
          )}

          {status === "LOW" && (
            <div className="flex flex-col items-start justify-between gap-4 rounded-[10px] border border-warning/40 bg-warning-bg p-4 sm:flex-row sm:items-center">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
                <div>
                  <p className="text-sm font-semibold text-ink">Your wallet balance is low</p>
                  <p className="mt-0.5 text-[13px] text-ink-secondary">
                    You can create approximately {remainingMandates} more mandates. Request a top-up to avoid
                    interruption.
                  </p>
                </div>
              </div>
              <Button onClick={() => setTopupOpen(true)} leftIcon={<Plus className="h-4 w-4" />}>
                Request Top-up
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                label: "Lifetime Credited",
                value: formatINR(balance?.lifetimeCredited ?? 0),
                sub: "Total added to your wallet",
              },
              {
                label: "Lifetime Spent",
                value: formatINR(balance?.lifetimeSpent ?? 0),
                sub: "Charges deducted so far",
              },
              {
                label: "Pending Top-ups",
                value: `${balance?.pendingTopups ?? 0}`,
                sub: "Awaiting admin approval",
              },
              {
                label: "Current Rate",
                value: `₹${creationRate} / ₹${installmentRate}`,
                sub: "Per mandate / per installment",
              },
            ].map((s) => (
              <div key={s.label} className="rounded-[10px] border border-line bg-card p-5 shadow-card">
                <p className="text-[13px] text-ink-secondary">{s.label}</p>
                <p className="mt-1.5 text-2xl font-bold text-ink">{s.value}</p>
                <p className="mt-1.5 text-xs text-ink-secondary">{s.sub}</p>
              </div>
            ))}
          </div>

          <div className="rounded-[10px] border border-line bg-card p-1 shadow-card">
            <div className="flex gap-1 overflow-x-auto">
              <button
                type="button"
                onClick={() => setTab("transactions")}
                className={`${tabBtn} ${tab === "transactions" ? "bg-primary-500 text-white" : "text-ink-secondary hover:bg-page hover:text-ink"}`}
              >
                Transactions
              </button>
              <button
                type="button"
                onClick={() => setTab("topup")}
                className={`${tabBtn} ${tab === "topup" ? "bg-primary-500 text-white" : "text-ink-secondary hover:bg-page hover:text-ink"}`}
              >
                Top-up Requests
              </button>
            </div>
          </div>

          {tab === "transactions" ? (
            <TransactionsTab
              loading={txnLoading}
              search={searchInput}
              onSearch={setSearchInput}
              type={type}
              onType={setType}
              range={range}
              onRange={setRange}
              rangeOpen={rangeOpen}
              onRangeOpen={setRangeOpen}
              onReset={resetFilters}
              filtered={txns}
              openMandate={(refNo) => navigate(`/retailer/mandates/${refNo}`)}
              page={txnPage}
              pageSize={txnPageSize}
              total={txnTotal}
              onPageChange={setTxnPage}
              onPageSizeChange={(n) => {
                setTxnPageSize(n);
                setTxnPage(1);
              }}
            />
          ) : (
            <TopupsTab
              loading={topupLoading}
              topups={topups}
              page={topupPage}
              pageSize={topupPageSize}
              total={topupTotal}
              onPageChange={setTopupPage}
              onPageSizeChange={(n) => {
                setTopupPageSize(n);
                setTopupPage(1);
              }}
              onNewRequest={() => setTopupOpen(true)}
              onViewDetail={(r) => {
                setDetailTarget(r);
                setDetailOpen(true);
              }}
            />
          )}
        </div>
      )}

      <RequestTopupModal open={topupOpen} onClose={() => setTopupOpen(false)} onSubmit={handleTopupSubmit} />
      <TopupDetailModal open={detailOpen} onClose={() => setDetailOpen(false)} request={detailTarget} />
    </div>
  );
}
